import { faCopy, faShare } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { enqueueNotification } from 'Utils/Helpers'
import { LabelContent } from 'V2Components'
import { AWS_CLOUDFORMATION_TEMPLATES_MAP, useAddAccountWizardContext } from 'features/clouds'
import { getAppEnvironment, getHTMLDataTestID } from 'infra/utils'
import { Button, IconButton, Label, RadioInput, TextInput, Typography } from 'procyon-ui'
import React from 'react'

const OrgStep = ({}) => {
  const { awsSpec, setAwsSpec } = useAddAccountWizardContext()

  return (
    <>
      <Typography variant='body-regular'>Is this an Org Account?</Typography>
      <div className='flex flex-col gap-2 mt-4'>
        <RadioInput
          data-testid={getHTMLDataTestID('clouds-aws-add', 'input', 'yes-org-account')}
          checked={awsSpec.orgAccount}
          label='Yes'
          onChange={() => setAwsSpec({ orgAccount: true })}
          value='yes'
        />
        <RadioInput
          data-testid={getHTMLDataTestID('clouds-aws-add', 'input', 'no-org-account')}
          checked={!awsSpec.orgAccount}
          label='No'
          onChange={() => setAwsSpec({ orgAccount: false })}
          value='no'
        />
      </div>
    </>
  )
}

const appEnv = getAppEnvironment()

const CFTemplate = ({}) => {
  const { awsSpec, setAwsSpec } = useAddAccountWizardContext()
  const { externalID } = awsSpec

  const getCT = () => {
    const type = awsSpec.orgAccount ? 'org' : 'standard'
    return AWS_CLOUDFORMATION_TEMPLATES_MAP[appEnv][type]
  }

  const CT = getCT()

  const handleOpen = () => {
    const createLink = `https://console.aws.amazon.com/cloudformation/home#/stacks/create/review?templateURL=${CT}&param_ExternalId=${externalID}&param_AutoProxyDeploy=Enabled`
    window.open(createLink, '_blank').focus()
  }

  const handleCopyTemplate = () => {
    navigator.clipboard.writeText(CT)
    enqueueNotification('Cloud Formation Template copied to clipboard!', 'info')
  }

  return (
    <div className='my-4'>
      <Typography variant='body-regular'>
        Use the below cloud formation template and ExternalID details to create a role on AWS
        console or click on the{' '}
        <strong>
          {' '}
          <FontAwesomeIcon icon={faShare} /> Open CF Template
        </strong>{' '}
        button below to navigate to AWS console directly.
      </Typography>
      <div className='mt-2 flex gap-2 items-center'>
        <TextInput
          data-testid={getHTMLDataTestID('clouds-aws-add', 'input', 'copy-cf-template')}
          onClick={handleCopyTemplate}
          sx={{ width: '100%' }}
          readOnly
          value={CT}
        />
        <IconButton
          data-testid={getHTMLDataTestID('clouds-aws-add', 'button', 'copy-cf-template')}
          onClick={handleCopyTemplate}
          variant='clear'
          icon={faCopy}
        />
      </div>
      <div className='mt-2'>
        <TextInput
          data-testid={getHTMLDataTestID('clouds-aws-add', 'input', 'external-id')}
          value={externalID}
          onChange={(e) => setAwsSpec({ externalID: e.target.value })}
          sx={{ width: '100%' }}
          label='Use the following External ID to create role.'
        />
      </div>
      <Typography className='my-2 text-center' variant='caption-regular'>
        Or
      </Typography>
      <Button
        data-testid={getHTMLDataTestID('clouds-aws-add', 'input', 'open-cf-template')}
        onClick={handleOpen}
        className='mx-auto'
        variant='primary'
        icon={faShare}
      >
        Open CF Template
      </Button>
    </div>
  )
}

const ARNOuIDStep = ({}) => {
  const { awsSpec, setAwsSpec } = useAddAccountWizardContext()
  const { arn, orgAccount, ouID } = awsSpec
  return (
    <>
      <Typography variant='body-regular'>
        {awsSpec.orgAccount ? (
          <>
            Please retrieve the <strong>Amazon Resource Name (ARN)</strong> from the 'Output' tab
            and the <strong>Organizational Unit Identifier (OUID)</strong> from the 'Parameters' tab
            of the CloudFormation stack that was previously created on AWS and enter them below.
          </>
        ) : (
          <>
            Please retrieve the <strong>Amazon Resource Name (ARN)</strong> from the 'Output' tab of
            the CloudFormation stack that was previously created on AWS and enter them below.
          </>
        )}
      </Typography>
      <div className='mt-4'>
        <TextInput
          data-testid={getHTMLDataTestID('clouds-aws-add', 'input', 'arn')}
          value={arn}
          onChange={(e) => setAwsSpec({ arn: e.target.value })}
          sx={{ width: '100%' }}
          label='Cross Account Role ARN*'
        />
      </div>
      {orgAccount && (
        <div className='mt-2'>
          <TextInput
            data-testid={getHTMLDataTestID('clouds-aws-add', 'input', 'ouid')}
            value={ouID}
            onChange={(e) => setAwsSpec({ ouID: e.target.value })}
            sx={{ width: '100%' }}
            label='Organization Unit (OU) ID*'
          />
        </div>
      )}
    </>
  )
}

const AccountSummary = ({}) => {
  const { awsSpec, name, description, org, isEditMode } = useAddAccountWizardContext()
  return (
    <div>
      <Typography variant='body-regular'>Summary of Account</Typography>
      <LabelContent
        dataTestId={'account-name'}
        data-testid={name}
        title='Account Name'
        content={name}
      />
      <LabelContent
        dataTestId={'account-summary'}
        data-testid={description}
        title='Description'
        content={description}
      />
      <LabelContent
        dataTestId={'is-org-account'}
        title='Is Org Account'
        data-testid={awsSpec.orgAccount ? 'Yes' : 'No'}
        content={
          <Label
            variant={awsSpec.orgAccount ? 'grayBlue' : 'gray'}
            text={awsSpec.orgAccount ? 'Yes' : 'No'}
          />
        }
      />
      <LabelContent
        dataTestId={'arn'}
        data-testid={awsSpec.arn}
        title='ARN'
        content={awsSpec.arn}
      />
      <LabelContent
        dataTestId={'external-id'}
        data-testid={awsSpec.externalID}
        title='External ID'
        content={awsSpec.externalID}
      />
      {awsSpec.orgAccount && (
        <LabelContent
          dataTestId={'org-unit-id'}
          title='Organization Unit (OU) ID'
          data-testid={awsSpec.ouID}
          content={awsSpec.ouID}
        />
      )}
    </div>
  )
}

export const useAwsSteps = () => {
  const { isEditMode } = useAddAccountWizardContext()

  return [
    !isEditMode
      ? {
          label: 'Org Setup',
          content: <OrgStep />
        }
      : null,
    {
      label: 'CF Setup',
      content: <CFTemplate />
    },
    {
      label: 'ARN, External ID and OuID Setup',
      content: <ARNOuIDStep />
    },
    {
      label: 'Summary',
      content: <AccountSummary />
    }
  ].filter((e) => e)
}
